import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AccountLayout from "../layouts/AccountLayout";
import "./../styles/order-detail.css";
import { FaArrowCircleLeft, FaBoxOpen } from "react-icons/fa";
import { orderStatusStep, productTypeText, statusText } from "../utils";
import { HashLoader } from "react-spinners";
import ToastNotification, {
  showToast,
} from "../components/toasts/ToastNotification";
import { getOrder } from "../api/order.api";
import { useUser } from "../context/UserContext";
import socket from "../services/socket";
import FeedbackModal from "../components/modal/feedback-modal";
import { MdOutlineInsertComment } from "react-icons/md";
import StarRating from "./../components/star-rating/star-rating";
import { FiAlertTriangle, FiClock, FiTag } from "react-icons/fi";

export const OrderDetail = () => {
  const { user } = useUser();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();

  const [isInvalidOrder, setIsInvalidOrder] = useState(false);
  const [isProductNotFound, setIsProductNotFound] = useState(false);
  const [isReviewed, setIsReviewed] = useState(false);
  const [isFeedbackModal, setIsFeedbackModal] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, []);

  const handleMarkOrderStatus = useCallback(async ({ order_status }) => {
    setOrder((prevOrder) => ({
      ...prevOrder,
      order_status: order_status,
    }));
    setCurrentStep(orderStatusStep(order_status));
  }, []);

  useEffect(() => {
    if (user?._id && socket) {
      socket.on("markAsSuccess", handleMarkOrderStatus);
      socket.on("markAsFailed", handleMarkOrderStatus);

      return () => {
        socket.off("markAsSuccess", handleMarkOrderStatus);
        socket.off("markAsFailed", handleMarkOrderStatus);
      };
    }
  }, [user?._id, handleMarkOrderStatus, socket]);

  const fetchOrder = async () => {
    setLoading(true);
    setIsInvalidOrder(false);
    setIsProductNotFound(false);

    try {
      const res = await getOrder(orderId);
      const foundOrder = res?.data?.order || null;

      if (res?.success) {
        const missingProduct =
          !foundOrder?.product ||
          foundOrder?.product === "notfound" ||
          String(foundOrder?.product?.product_name || "").toLowerCase() === "notfound";

        if (missingProduct) {
          setIsProductNotFound(true);
          return;
        }

        setOrder(foundOrder);
        setCurrentStep(orderStatusStep(foundOrder?.order_status));
        setIsReviewed(Boolean(foundOrder?.review));
        setIsInvalidOrder(!foundOrder);
      }
    } catch (error) {
      const normalizedMessage = String(error?.message || "").toLowerCase();
      if (normalizedMessage.includes("product not found")) {
        setIsProductNotFound(true);
      } else {
        showToast(error.message, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  if (isProductNotFound) {
    return (
      <AccountLayout title="Đơn hàng">
        <div className="client-order-detail-page">
          <div className="order-detail-empty-state order-detail-error-state">
            <FiAlertTriangle className="order-detail-empty-icon order-detail-error-icon" />
            <p>Sản phẩm của đơn hàng này không còn tồn tại.</p>
            <button
              className="order-detail-back-btn"
              onClick={() => navigate("/order")}
            >
              Quay lại danh sách đơn hàng
            </button>
          </div>
        </div>
      </AccountLayout>
    );
  }

  const order_package = order?.product?.product_attributes?.packages?.find(
    (pack) => String(pack._id) === String(order?.order_attributes?.packageId)
  );

  const renderedAttributes = () => {
    if (!order) return null;
    if (order?.order_type === "topup_package") {
      return (
        <div className="order-detail-attribute-row">
          <span>Gói nạp</span>
          <strong>{order_package?.name || "-"}</strong>
        </div>
      );
    }

    if (
      order?.order_type === "gift_card" ||
      (order?.order_type === "game_account" && !order?.product?.isValuable)
    ) {
      return (
        <>
          <div className="order-detail-attribute-row">
            <span>Tài khoản</span>
            <strong>{order?.order_attributes?.username || "-"}</strong>
          </div>
          <div className="order-detail-attribute-row">
            <span>Mật khẩu</span>
            <strong>{order?.order_attributes?.password || "-"}</strong>
          </div>
        </>
      );
    }

    if (order?.order_type === "utility_account" && !order?.product?.isValuable) {
      return Array.isArray(order?.order_attributes)
        ? order.order_attributes.map((od, index) => (
            <div className="order-detail-attribute-row" key={`${od.label}-${index}`}>
              <span>{od.label || "Thuộc tính"}</span>
              <strong>{od.value || "-"}</strong>
            </div>
          ))
        : null;
    }

    return null;
  };

  const orderSteps = [
    { id: 0, title: "Đặt hàng" },
    { id: 1, title: "Đang xử lí" },
    { id: 2, title: order?.order_status === "processing" ? "Hoàn tất" : statusText(order?.order_status) },
  ];
  const timelineTone =
    order?.order_status === "success"
      ? "success"
      : order?.order_status === "failed"
        ? "failed"
        : "processing";
  const productImage = order_package?.img || order?.product?.product_imgs?.[0] || "";

  if (isInvalidOrder) {
    return (
      <AccountLayout title="Đơn hàng">
        <div className="client-order-detail-page">
          <div className="order-detail-empty-state">
            <FaBoxOpen className="order-detail-empty-icon" />
            <p>Đơn hàng không tồn tại hoặc đã bị xóa.</p>
            <button
              className="order-detail-back-btn"
              onClick={() => navigate("/order")}
            >
              Quay lại danh sách đơn hàng
            </button>
          </div>
        </div>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout title="Đơn hàng">
      <div className="client-order-detail-page">
        <ToastNotification />
        <FeedbackModal
          isOpen={isFeedbackModal}
          onClose={() => setIsFeedbackModal(false)}
          productId={order?.productId}
          orderId={order?.orderId}
          setIsReviewed={setIsReviewed}
          setOrder={setOrder}
        />
        {loading ? (
          <div className="loader-container">
            <HashLoader color="var(--hash-loader-color)" />
          </div>
        ) : (
          <>
            <section className="order-detail-hero">
              <div className="order-detail-hero-main">
                <button
                  className="order-detail-back-btn order-detail-hero-back"
                  onClick={() => navigate("/order")}
                >
                  <FaArrowCircleLeft />
                  Quay lại
                </button>
                <div className="order-detail-title-wrap">
                  <p className="order-detail-eyebrow">Order Detail</p>
                  <h2 className="order-detail-order-id">#{orderId}</h2>
                  <p className="order-detail-time">
                    Đặt hàng lúc {new Date(order?.createdAt).toLocaleString() || ""}
                  </p>
                </div>
              </div>
              <div className="order-detail-hero-status">
                <p>Trạng thái</p>
                <span className={`order-detail-status-chip order-status-${order?.order_status}`}>
                  {statusText(order?.order_status)}
                </span>
              </div>
            </section>

            <section className="order-detail-stepper-shell">
              <div className="order-progress-stepper">
                {orderSteps.map((step, index) => {
                  const isCompleted = step.id < currentStep;
                  const isCurrent = step.id === currentStep;
                  const isLast = index === orderSteps.length - 1;

                  return (
                    <div key={step.id} className="order-progress-step">
                      <div
                        className={`order-progress-node ${isCompleted ? "completed" : ""} ${isCurrent ? "current" : ""} ${isCurrent && step.id === 2 ? `timeline-${timelineTone}` : ""}`}
                      >
                        {step.id + 1}
                      </div>
                      <p
                        className={`order-progress-label ${isCompleted || isCurrent ? "active" : ""} ${isCurrent && step.id === 2 ? `timeline-${timelineTone}` : ""}`}
                      >
                        {step.title}
                      </p>
                      {!isLast && (
                        <span
                          className={`order-progress-line ${step.id < currentStep ? "active" : ""} ${step.id === 1 && currentStep === 2 ? `timeline-${timelineTone}` : ""}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="order-detail-layout">
              <div className="order-detail-left-col">
                <article className="order-detail-card">
                  <div className="order-detail-card-head">
                    <FaBoxOpen />
                    <h3>Thông tin sản phẩm</h3>
                  </div>
                  <div className="order-detail-product-showcase">
                    <div className="order-detail-product-image-wrap">
                      <img
                        className="order-detail-product-image"
                        src={productImage}
                        alt="product-image"
                      />
                    </div>
                    <div className="order-detail-product-content">
                      <div className="order-detail-product-badge-row">
                        <span className="order-detail-product-badge">{productTypeText(order?.order_type) || "Sản phẩm"}</span>
                        <span className={`order-detail-product-badge order-status-${order?.order_status}`}>
                          {statusText(order?.order_status)}
                        </span>
                      </div>
                      <p className="order-detail-product-name">
                        {order?.product?.product_name || "Sản phẩm"}
                      </p>
                      <div className="order-detail-product-attributes">
                        {renderedAttributes()}
                      </div>
                    </div>
                  </div>
                </article>

                {order?.order_status === "success" && (
                  <button
                    disabled={isReviewed}
                    onClick={() => setIsFeedbackModal(true)}
                    className={`write-review-button ${isReviewed ? "disabled-review-button" : ""}`}
                  >
                    {isReviewed ? "Đơn hàng đã được đánh giá" : "Đánh giá đơn hàng"}
                  </button>
                )}

                {isReviewed && (
                  <div className="order-detail-review-container">
                    <div className="order-detail-review-header">
                      <MdOutlineInsertComment className="order-detail-review-header-icon" />
                      <p className="order-detail-review-header-text">Đánh giá của bạn</p>
                    </div>
                    <div className="comment-rating-container">
                      <p className="order-detail-comment">{order?.review?.comment || "No comment"}</p>
                      <StarRating rating={order?.review?.rating || 5} />
                    </div>
                  </div>
                )}
              </div>

              <div className="order-detail-right-col">
                <article className="order-detail-card order-detail-log-card">
                  <div className="order-detail-card-head">
                    <FiClock />
                    <h3>Timeline đơn hàng</h3>
                  </div>
                  <div className="order-detail-log-list">
                    <p className="order-detail-log-item">
                      Đơn hàng được tạo vào lúc
                      <span>{new Date(order?.createdAt).toLocaleString()}</span>
                    </p>
                    <p className="order-detail-log-item">
                      Đơn hàng cập nhật vào lúc
                      <span>{new Date(order?.updatedAt).toLocaleString()}</span>
                    </p>
                    {order?.discountCode && (
                      <p className="order-detail-log-item">
                        Đơn hàng sử dụng mã
                        <span>{order?.discountCode}</span>
                      </p>
                    )}
                    <p className="order-detail-log-item">
                      Nội dung
                      <span>{order?.order_note || "Không có ghi chú"}</span>
                    </p>
                  </div>
                </article>

                <article className="order-detail-card">
                  <div className="order-detail-card-head">
                    <FiTag />
                    <h3>Chi tiết thanh toán</h3>
                  </div>
                  <div className="order-summary-container order-price-section-container">
                    <div className="order-summary-line">
                      <span>Tạm tính:</span>
                      <span>{order?.order_base_amount?.toLocaleString() || 0}đ</span>
                    </div>
                    <div className="order-summary-line">
                      <span>Giảm giá:</span>
                      <span>-{order?.order_discount_amount?.toLocaleString() || 0}đ</span>
                    </div>
                    <div className="order-summary-total">
                      <strong>Tổng đã thanh toán:</strong>
                      <strong>{order?.order_final_amount?.toLocaleString() || 0}đ</strong>
                    </div>
                  </div>
                </article>
              </div>
            </section>
          </>
        )}
      </div>
    </AccountLayout>
  );
};
